import { config } from '../core/config.js';
import { eventBus } from '../core/eventBus.js';

export class Hero {
  constructor() {
    this.particlesLoaded = false;
    this.typewriterElement = document.querySelector('.hero-text h2');
    this.titles = [
      '\u00A0AI Researcher',
      '\u00A0AI Automation Engineer'
    ];
    this.currentTitleIndex = 0;
    this.charIndex = 0;
    this.isDeleting = false;
    this.typingSpeed = 100;
    
    this.init();
  }
  
  init() {
    this.initParticles();
    this.initTypewriter();
    this.setupScrollIndicator();
  }
  
  async initParticles() {
    const particlesContainer = document.getElementById('particles-js');
    if (!particlesContainer) return;
    
    // Wait for particles.js to load
    await this.waitForParticlesJS();
    
    if (window.particlesJS) {
      particlesJS('particles-js', config.particles);
      this.particlesLoaded = true;
      
      // Update particles on theme change
      eventBus.on('theme:changed', () => this.updateParticlesTheme());
    }
  }
  
  waitForParticlesJS() {
    return new Promise((resolve) => {
      if (window.particlesJS) {
        resolve();
        return;
      }
      
      // Check every 100ms for up to 5 seconds
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (window.particlesJS || attempts > 50) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }
  
  updateParticlesTheme() {
    if (!this.particlesLoaded || !window.pJSDom || !window.pJSDom[0]) return;
    
    const isDark = document.body.getAttribute('data-theme') === 'dark';
    const particleColor = isDark ? '#818CF8' : '#4338CA';
    const lineColor = isDark ? '#818CF8' : '#4338CA';
    
    try {
      const pJS = window.pJSDom[0].pJS;
      if (pJS && pJS.particles) {
        pJS.particles.color.value = particleColor;
        pJS.particles.line_linked.color = lineColor;
        pJS.tmp.obj.line_linked_color_rgb_line = hexToRgb(lineColor);
      }
    } catch (error) {
      console.warn('Could not update particles theme:', error);
    }
  }
  
  initTypewriter() {
    if (!this.typewriterElement) return;
    
    const type = () => {
      const currentTitle = this.titles[this.currentTitleIndex];
      
      if (this.isDeleting) {
        // Deleting text
        this.typewriterElement.textContent = currentTitle.substring(0, this.charIndex - 1);
        this.charIndex--;
        this.typingSpeed = 50;
      } else {
        // Typing text
        this.typewriterElement.textContent = currentTitle.substring(0, this.charIndex + 1);
        this.charIndex++;
        this.typingSpeed = 100;
      }
      
      // If finished typing current title
      if (!this.isDeleting && this.charIndex === currentTitle.length) {
        this.isDeleting = true;
        this.typingSpeed = 1500; // Pause at end
      } 
      // If finished deleting current title
      else if (this.isDeleting && this.charIndex === 1) {
        this.isDeleting = false;
        this.currentTitleIndex = (this.currentTitleIndex + 1) % this.titles.length;
        this.typingSpeed = 500; // Pause before next
      }
      
      setTimeout(type, this.typingSpeed);
    };
    
    // Start typewriter after delay
    setTimeout(type, 1000);
  }
  
  setupScrollIndicator() {
    const scrollIndicator = document.querySelector('.scroll-indicator a');
    if (!scrollIndicator) return;
    
    scrollIndicator.addEventListener('click', (e) => {
      e.preventDefault();
      
      const target = scrollIndicator.getAttribute('href');
      const targetSection = document.querySelector(target);
      
      if (targetSection) {
        targetSection.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }
}

// Helper function
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}